import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import registry from "../../data/blogMetadata.json";

const normalizePath = (value = "/") => {
  const path = value.replace(/\/+$/, "") || "/";
  return path.toLowerCase();
};

const postsByPath = Object.values(registry.posts).reduce((posts, post) => {
  posts[normalizePath(post.canonicalPath)] = post;
  return posts;
}, {});

const absoluteUrl = (path) => `${registry.siteUrl}${path}`;

const BlogMetadata = () => {
  const { pathname } = useLocation();
  const normalizedPath = normalizePath(pathname);
  const entry = normalizedPath === "/blog" ? registry.index : postsByPath[normalizedPath];

  if (!entry) {
    return null;
  }

  const image = entry.ogImage || registry.fallbackImage.path;
  const imageAlt = entry.imageAlt || registry.fallbackImage.alt;
  const imageUrl = absoluteUrl(image);
  const canonicalUrl = absoluteUrl(entry.canonicalPath);
  const description = entry.socialDescription || entry.description;
  const type = entry.type || "article";
  const author = entry.author || registry.defaultAuthor;

  return (
    <Helmet prioritizeSeoTags>
      <title>{entry.title}</title>
      <meta name="description" content={entry.description} />
      <meta name="keywords" content={entry.keywords} />
      <meta name="robots" content={registry.robots} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:site_name" content={registry.siteName} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={entry.title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:image:type" content={registry.fallbackImage.type} />
      <meta property="og:image:width" content={String(registry.fallbackImage.width)} />
      <meta property="og:image:height" content={String(registry.fallbackImage.height)} />
      <meta property="og:image:alt" content={imageAlt} />
      {type === "article" && author ? <meta property="article:author" content={author} /> : null}
      {entry.publishedTime ? <meta property="article:published_time" content={entry.publishedTime} /> : null}
      {entry.modifiedTime ? <meta property="article:modified_time" content={entry.modifiedTime} /> : null}
      {author ? <meta name="author" content={author} /> : null}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={entry.title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={imageAlt} />
    </Helmet>
  );
};

export default BlogMetadata;
